import { useContext, useEffect, useState } from "react";
import { resetUser, updateMessage, updateUserIntialState } from "../../_features";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash, FaExclamationCircle } from "react-icons/fa";
import { Button, Loader, Message, TextInput } from "../../components";
import { AuthService } from "../../_service";
import { Constant } from "../../config";
import { useAppDispatch, useAppSelector } from "../../_app";
import CreatePassword from "./CreatePassword";
import ResetPassword from "./ResetPassword";
import Signin from "./Signin";
import OtpVerification, { OtpChallenge } from "./OtpVerification";
import { AuthHeader } from "../../components/header";
import useFirebaseAnalytics from "../../hooks/useFirebaseAnalytics";
import moment from "moment";

import {
  BackToHomeLink,
  BackToHomeLinkWrap,
  Container,
  ContainerBox,
  DontRememberLink,
  DontRememberLinkSection,
  InputFieldWrapper,
  Title,
} from "./AuthStyles";
import AccountLockedNotice from "./AccountLockedNotice";
import { useCountdown } from "../../hooks";
import { TrustedDevice, Utils } from "../../utils";
import { Icon } from "@mui/material";
import { microAgent } from "../../newRelicConfig";
import { is } from "date-fns/locale";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user);
  const menu = useAppSelector((state) => state.user.menu);
  const loginMessage = useAppSelector((state) => state.message.login);
  const resetMessage = () => {
    dispatch(updateMessage({ message: "", page: "login", type: "error" }));
  };
  
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const logFirebaseEvent = useFirebaseAnalytics();
  const [showPassword, setShowPassword] = useState(false);

  const [showMessage, setShowMessage] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const [role, setRole] = useState("admin");  // admin or employee

  // When the backend answers a login with `otp_required`, we hold the challenge
  // here and swap the credential form for the OTP screen.
  const [otpChallenge, setOtpChallenge] = useState<OtpChallenge | null>(null);

  // Set when the backend reports the account is temporarily locked
  // (`retry_after_minutes`); holds the epoch ms the lock lifts at.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const lockRemaining = useCountdown(lockedUntil);
  const isLocked = lockRemaining > 0;

  console.log("Search Params = ", searchParams, Object.fromEntries(searchParams?.entries() || []));

  // let hostname = window.location.hostname?.split('.')[0];

  let isEmployee = window.location.hostname?.split('.')[0]?.startsWith('emp');




  const togglePassword = () => {
    setShowPassword(!showPassword);

  };

  const checkIfEmployee = (data:any) => {
    let groups = data.user.groups?.map((x:any) => x.name);
    console.log("Groups = ", groups, groups?.length === 1, groups[0], groups[0] === "Employees");
    if (groups?.length === 1 && groups[0] === "Employees") {
      console.log('inside groups, navigating', groups, groups[0]);
      return true;
    } else {
      return false;
    }
  }
  

  useEffect(() => {
    if (user?.name) {
      if (menu?.includes("dashboard")) {
        navigate("/dashboard/request/data-entry");
      } else {
        navigate("/dashboard/manage-company");
      }
    }
  }, [user.name]);

  useEffect(() => {
    console.log("Timeout useEffect called")
    const storedShowLogoutMessage = localStorage.getItem('showLogoutMessage') === 'true';
    
    let timeoutId: NodeJS.Timeout;
    if (storedShowLogoutMessage) {
      localStorage.removeItem('showLogoutMessage');
      setShowMessage(true);
      
      timeoutId = setTimeout(() => {
        setShowMessage(false);
      }, 10000);
    }

    // Cleanup timeout on component unmount or when dependency changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  /**
   * Applies a completed login payload to the store. Shared by the direct login
   * path (trusted device / OTP disabled) and the OTP verification path, since
   * the backend returns the identical payload in both cases.
   */
  const completeLogin = (data: any) => {
    Utils.resetStoreData();

    if (!data?.user?.email) {
      dispatch(
        updateMessage({
          message: `Couldn't log you in as '${email}'. Please try again or reset your password if this persists.`,
          page: "login",
          type: "error",
        })
      );
      return;
    }

    let key = "login_user";
    let logData = {
      user_id: data.user.id,
      name: data.user.name,
      // device_fingerprint: "using_client_js",
      event_date_time: moment().utc().format("DD/MM/YYYY hh:mm A"),
      platform: "web",
    };
    // logFirebaseEvent(key, logData);
    microAgent.addPageAction(key, logData);

    dispatch(
      updateUserIntialState({
        email: data.user.email,
        id: data.user.id,
        name: data.user.name,
        profile_image: data.user.profile_image,
        refresh: data.refresh,
        access: data.access,
        defaultCompany:
          data?.default_company?.default_company_info?.name || "undefined",
        defaultCompanyPayrunStartDate:
          data?.default_company?.default_company_info?.payrun_start_date,
        defaultCompnayId: data?.default_company?.default_company,
        actualDefaultCompanyId: data?.default_company?.default_company,
        lastLoggedInTime: Date.now(),
        lastActivityTime: Date.now(),
        isSessionActive: true,
        menu: data.menu,
        menu_permission: data.menu_permission,
        groups: data.user.groups,
        is_superuser: data.user.is_superuser,
        permission:
          data?.default_company?.access && data?.default_company?.access !== null
            ? data?.default_company?.access
            : "full",
        current_payrun_date:
          data?.default_company?.default_company_info?.current_payrun_date ||
          data?.default_company?.default_company_info?.payrun_start_date,
        isEmployee: isEmployee,
      })
    );
    sessionStorage.setItem("activeTab", "true");
  };

  /** Called by the OTP screen once the code is accepted. */
  const handleOtpVerified = (data: any) => {
    setOtpChallenge(null);
    completeLogin(data);
  };

  /** Called when the user backs out of the OTP step. */
  const handleOtpCancel = () => {
    setOtpChallenge(null);
    setPassword("");
    resetMessage();
  };

  /**
   * The lockout is per account, so clearing the credentials of the locked one
   * lifts the notice and re-opens the form for somebody else to sign in.
   */
  const handleUseAnotherAccount = () => {
    setLockedUntil(null);
    setEmail("");
    setPassword("");
    resetMessage();
  };

  const handleLogin = async () => {
    // Locked out - the notice already says when to come back.
    if (isLocked) return;
    // The lock has elapsed; drop its notice now that they're trying again.
    if (lockedUntil) setLockedUntil(null);

    if (email === "") {
      dispatch(
        updateMessage({
          message: `Email field can not be empty`,
          page: "login",
          type: "error",
        })
      );
    } else if (password === "") {
      dispatch(
        updateMessage({
          message: `Password field can not be empty`,
          page: "login",
          type: "error",
        })
      );
    } else {
      // A device the user previously chose to trust lets the backend skip OTP.
      const deviceToken = TrustedDevice.getDeviceToken(email);

      try {
        setSending(true);
        const res = isEmployee ? await AuthService._employeeLogin({
          email: email,
          password,
          ...(deviceToken ? { device_token: deviceToken } : {}),
          ...Object.fromEntries(searchParams?.entries() || []),
        })  : await AuthService._userLogin({
          username: email,
          password,
          ...(deviceToken ? { device_token: deviceToken } : {}),
          ...Object.fromEntries(searchParams?.entries() || []),
        });
        if (res.status === 200) {
          console.log("Server = ", process.env.SERVER);

          if (res.data?.otp_required) {
            // Credentials are valid but the login needs a second factor. If we
            // sent a device token and still got here, that token is dead.
            if (deviceToken) TrustedDevice.clearDeviceToken(email);

            resetMessage();
            setOtpChallenge({
              email,
              otp_expiry_minutes: res.data.otp_expiry_minutes,
              resend_cooldown_seconds: res.data.resend_cooldown_seconds,
              otp_length: res.data.otp_length,
              verification_token: res.data.verification_token,
              message: res.data.message,
            });
            setSending(false);
            return;
          }

          // if(checkIfEmployee(res.data)) {
          //   dispatch(resetUser());
          //   navigate("/mobile-only", { replace: true });
          // } else
          completeLogin(res.data);
        } else {
          dispatch(
            updateMessage({
              message: `Couldn't log you in as '${email}'. Please try again or reset your password if this persists.`,
              page: "login",
              type: "error",
            })
          );
        }
        setSending(true);
      } catch (e: any) {
        setSending(false);

        const data = e?.response?.data;

        // A lockout is explained by the countdown notice instead of the generic
        // error banner - the backend's fixed "after 30 minutes" wording would
        // contradict the live timer.
        if (data?.retry_after_minutes !== undefined) {
          resetMessage();
          setLockedUntil(
            Date.now() + (Number(data.retry_after_minutes) + 1) * 60 * 1000
          );
          return;
        }

        dispatch(
          updateMessage({
            message: isEmployee ? e?.response?.data?.message  : `Couldn't log you in as '${email}'. Please try again or reset your password if this persists.`,
            page: "login",
            type: "error",
          })
        );
      }
    }
  };


  console.log('isEmployee = ', isEmployee, window.location.hostname);

  // Step 2 of the login flow - credentials accepted, waiting on the emailed OTP.
  if (otpChallenge) {
    return (
      <>
        <AuthHeader />
        <OtpVerification
          challenge={otpChallenge}
          isEmployee={isEmployee}
          onVerified={handleOtpVerified}
          onCancel={handleOtpCancel}
        />
      </>
    );
  }

  return (
    <>
      {sending && <Loader />}
      <AuthHeader />

      {showMessage && <div style={{
        textAlign: 'center',
        padding: '24px',
        margin: '3rem auto',
        marginBottom: 0,
        maxWidth: '40%',
        backgroundColor: '#e8f4ff',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #b8daff',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <FaExclamationCircle size={32} style={{ color: '#004085' }} />
        <div>
          <p style={{
            color: '#004085',
            fontSize: '1rem',
            lineHeight: '1.6',
            margin: 0,
            fontWeight: '500',
            textAlign: 'left'
          }}>
            You have been inactive for a while. We have logged you out for security reasons.
            <br />
            Please sign in again to resume your session.
          </p>
        </div>
      </div>}

      <Container>
        <ContainerBox>
          <Title>{Constant.login.login + " as "} 
            <strong style={{
              borderBottom: '2px solid #63C8FF',
              paddingLeft: '2px',
              paddingRight: '2px',
              paddingBottom: '2px',
            }}>{isEmployee ? " Employee" : " Admin"}</strong>
          </Title>
          <InputFieldWrapper>
            <Message data={loginMessage} close={resetMessage} />
            {lockedUntil !== null && (
              <AccountLockedNotice
                remaining={lockRemaining}
                reason={Constant.lockout.login_reason}
                showResetHint
                actionLabel={Constant.lockout.use_another_account}
                onAction={handleUseAnotherAccount}
              />
            )}
            <TextInput
              labal={Constant.login.email}
              type="email"
              placeholder="Email address associated with your account"
              value={email}
              onChange={setEmail}
              style={{ textTransform: "none" }}
              containerStyle={{ marginTop: "1rem" }}
            />

            <DontRememberLink tabIndex={-1}  to="/password-resets">
              {Constant.login.forgot_password}
            </DontRememberLink>
            <DontRememberLinkSection>

              <div style={{
                margin: "0 auto",
                borderColor: "#000",
                borderWidth: "1px"
              }}>
                <div style={{
                  width: " 100%",
                  position: "relative"
                }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'absolute',
                    height: '100%',
                    top: "1rem",
                    right: "1rem",
                    paddingRight: '1px',
                  }}>
                    {(showPassword)
                      ? <FaEyeSlash onClick={togglePassword} style={{ cursor: 'pointer' }} />
                      : <FaEye onClick={togglePassword} style={{ cursor: 'pointer' }} />}
                  </span>

                  <TextInput
                    labal={Constant.login.password}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password (leave blank if not chosen yet)"
                    value={password}
                    onChange={setPassword}
                    style={{
                      textTransform: "none",
                      display: 'block',
                      width: '100%',
                      height: '45px',
                      padding: '0 5px',
                      borderRadius: '2px',
                    }}
                    onSubmit={handleLogin}
                  />
                </div>
              </div>
            </DontRememberLinkSection>
            {/* <div style={{ marginTop: "1rem", marginBottom: "1rem", marginToop: "10rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 800, fontSize: "1rem" }}>
              Continue as
              </label>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                <input
                type="radio"
                name="userType"
                value="admin"
                onChange={(e) => setRole(e.target.value)}
                checked={role === "admin"}
                />
                <span>Admin</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                <input
                type="radio"
                name="userType"
                value="employee"
                onChange={(e) => setRole(e.target.value)}
                checked={role === "employee"}
                />
                <span>Employee</span>
              </label>
              </div>
            </div> */}
            <Button title={"Login"} onClick={handleLogin} disabled={isLocked} />
          </InputFieldWrapper>
          <BackToHomeLinkWrap>
            <BackToHomeLink to="https://tellboss.co/">
              {Constant.common.back_to_home_page}
            </BackToHomeLink>
          </BackToHomeLinkWrap>
        </ContainerBox>

      </Container>
      
    </>
  );
};

export default Login;
export { CreatePassword, ResetPassword, Signin };
